import React from 'react';

import { cn } from '../cn';
import Button from '../Input2/Button';
import Input from '../Input2/Input';
import { Arrows, Editor, Weather } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import { VOICE_COLOR } from './voiceModeStyles';

/** What the rep asked for: a dossier angle to lead with and/or a one-line steer. */
export type VoiceScriptRequest = {
  angle?: string;
  command?: string;
};

export type VoiceNoteStep = 'lead' | 'script' | 'deliver';

const MAX_ANGLES = 4;

const LABEL = 'text-[10px] font-semibold uppercase tracking-wide text-zumthor-700 dark:text-zumthor-300';

// ============================================================================
// Stepper — "1 Lead · 2 Take · 3 Send"
// ============================================================================

export type VoiceNoteStepperProps = {
  step: VoiceNoteStep;
  /** Steps the rep may jump back to (already completed). */
  reachable: VoiceNoteStep[];
  onStepClick: (step: VoiceNoteStep) => void;
};

const STEPS: Array<{ id: VoiceNoteStep; label: string }> = [
  { id: 'lead', label: 'Lead' },
  { id: 'script', label: 'Take' },
  { id: 'deliver', label: 'Send' },
];

export function VoiceNoteStepper({ step, reachable, onStepClick }: VoiceNoteStepperProps) {
  const currentIndex = STEPS.findIndex((s) => s.id === step);
  return (
    <ol className="flex items-center gap-1" aria-label="Voice note steps" data-testid="voice-stepper">
      {STEPS.map((s, index) => {
        const isCurrent = s.id === step;
        const isDone = index < currentIndex;
        const canJump = !isCurrent && reachable.includes(s.id);
        return (
          <li key={s.id} className="flex items-center gap-1">
            {index > 0 && <span className="h-px w-2 bg-zumthor-300 dark:bg-zumthor-800" aria-hidden="true" />}
            <button
              type="button"
              aria-current={isCurrent ? 'step' : undefined}
              disabled={!canJump}
              data-testid={`voice-step-${s.id}`}
              onClick={() => onStepClick(s.id)}
              className={cn(
                'flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium transition-colors',
                isCurrent && 'bg-zumthor-800 text-white dark:bg-zumthor-700',
                isDone && 'text-zumthor-800 dark:text-zumthor-200',
                !isCurrent && !isDone && 'text-zumthor-400 dark:text-zumthor-600',
                canJump && 'cursor-pointer hover:bg-zumthor-100 dark:hover:bg-zumthor-900',
                !canJump && 'cursor-default',
              )}>
              <span
                className={cn(
                  'flex size-4 items-center justify-center rounded-full text-[10px] tabular-nums',
                  isCurrent ? 'bg-white/25' : 'bg-zumthor-100 dark:bg-zumthor-900',
                )}>
                {index + 1}
              </span>
              {s.label}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ============================================================================
// Step 1 — Lead with
// ============================================================================

export type VoiceNoteLeadStepProps = {
  /** Dossier talking points, best first. The first is the dossier's "lead with this". */
  angles: string[];
  activeAngle: string | null;
  disabled?: boolean;
  onGenerate: (request: VoiceScriptRequest) => void;
};

/**
 * "What do you want to lead with?" — the dossier angles as full-text rows plus
 * a free-text line. Choosing either hands off to the takes step.
 */
export function VoiceNoteLeadStep({ angles, activeAngle, disabled = false, onGenerate }: VoiceNoteLeadStepProps) {
  const [steer, setSteer] = React.useState('');
  const shownAngles = angles.filter((a) => a.trim().length > 0).slice(0, MAX_ANGLES);

  const generateFromSteer = () => {
    const command = steer.trim();
    onGenerate({ command: command.length > 0 ? command : undefined });
  };

  return (
    <div className="flex flex-col gap-2 px-3 pb-3" data-testid="voice-lead-step">
      {shownAngles.length > 0 && (
        <div className="flex flex-col gap-1" role="group" aria-label="Lead with">
          <span className={LABEL}>From the dossier</span>
          {shownAngles.map((angle, index) => {
            const isActive = activeAngle === angle;
            return (
              <button
                key={angle}
                type="button"
                disabled={disabled}
                data-testid="voice-angle"
                aria-pressed={isActive}
                onClick={() => onGenerate({ angle })}
                className={cn(
                  'flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm leading-snug transition-colors cursor-pointer',
                  'hover:border-zumthor-400 hover:bg-white dark:hover:bg-zumthor-900/60',
                  isActive
                    ? 'border-zumthor-700 bg-white text-zumthor-900 dark:border-zumthor-400 dark:bg-zumthor-900 dark:text-zumthor-50'
                    : 'border-zumthor-200 bg-zumthor-50/60 text-zumthor-800 dark:border-zumthor-800 dark:bg-zumthor-950 dark:text-zumthor-200',
                  disabled && 'cursor-default opacity-60',
                )}>
                <span className="mt-0.5 shrink-0 text-zumthor-400 dark:text-zumthor-300">
                  {index === 0 ? <Weather.Lightning02 className="size-3.5" /> : <span className="block size-3.5" />}
                </span>
                <span className="line-clamp-2">{angle}</span>
                <Arrows.ChevronRight className="ml-auto mt-0.5 size-3.5 shrink-0 opacity-60" />
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-1">
        {shownAngles.length > 0 && <span className={LABEL}>Or your own</span>}
        <div className="flex items-center gap-1.5">
          <Input
            size="sm"
            className="flex-1 min-w-0"
            placeholder={shownAngles.length > 0 ? 'Tell it what to say…' : 'What should this voice note say?'}
            value={steer}
            disabled={disabled}
            data-testid="voice-steer-input"
            onChange={(e) => setSteer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !disabled) {
                e.preventDefault();
                generateFromSteer();
              }
            }}
          />
          <Button
            size="sm"
            color={VOICE_COLOR}
            disabled={disabled}
            tooltip="Write three takes"
            data-testid="voice-write-button"
            icon={<Editor.Wand className="size-4" />}
            onClick={generateFromSteer}>
            Write it
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 2 — Pick a take
// ============================================================================

export type VoiceNoteTakesStepProps = {
  takes: string[];
  /** Index into `takes` of the one currently in the script box, or null. */
  selectedTake: number | null;
  isGenerating: boolean;
  error?: string | null;
  disabled?: boolean;
  onPickTake: (index: number) => void;
  /** Re-run the last request for three more takes. */
  onRegenerate?: () => void;
};

export function VoiceNoteTakesStep({
  takes,
  selectedTake,
  isGenerating,
  error,
  disabled = false,
  onPickTake,
  onRegenerate,
}: VoiceNoteTakesStepProps) {
  const busy = disabled || isGenerating;
  return (
    <div className="flex flex-col gap-2 px-3 pb-3" data-testid="voice-takes-step">
      {isGenerating && (
        <div
          className="flex items-center gap-2 py-2 text-sm text-zumthor-800 dark:text-zumthor-300"
          data-testid="voice-script-writing">
          <WaveSpinner className="size-4" />
          <span>Writing three takes…</span>
        </div>
      )}

      {error != null && !isGenerating && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert" data-testid="voice-script-error">
          {error}
        </p>
      )}

      {takes.length > 0 && !isGenerating && (
        <div className="flex flex-col gap-1" role="radiogroup" aria-label="Spoken takes">
          {takes.map((take, index) => {
            const isSelected = selectedTake === index;
            return (
              <button
                key={`${index}-${take.slice(0, 24)}`}
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={busy}
                data-testid="voice-take"
                onClick={() => onPickTake(index)}
                className={cn(
                  'group/take flex gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm leading-snug transition-colors cursor-pointer',
                  'hover:border-zumthor-400 hover:bg-white dark:hover:bg-zumthor-900/60',
                  isSelected
                    ? 'border-zumthor-700 bg-white text-zumthor-900 dark:border-zumthor-400 dark:bg-zumthor-900 dark:text-zumthor-50'
                    : 'border-zumthor-200 bg-zumthor-50/60 text-zumthor-800 dark:border-zumthor-800 dark:bg-zumthor-950 dark:text-zumthor-200',
                  busy && 'cursor-default opacity-60',
                )}>
                <span className="mt-px shrink-0 text-xs font-semibold tabular-nums text-zumthor-400 dark:text-zumthor-300">
                  {index + 1}
                </span>
                <span className="flex-1">{take}</span>
                <span className="mt-px shrink-0 text-[11px] font-medium text-zumthor-700 opacity-0 transition-opacity group-hover/take:opacity-100 dark:text-zumthor-300">
                  Use this →
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!isGenerating && (takes.length > 0 || error != null) && onRegenerate != null && (
        <div className="flex justify-end">
          <Button
            size="2xs"
            text
            color={VOICE_COLOR}
            disabled={busy}
            data-testid="voice-more-takes"
            icon={<Arrows.RefreshCw04 className="size-3" />}
            onClick={onRegenerate}>
            {error != null && takes.length === 0 ? 'Try again' : 'Three more'}
          </Button>
        </div>
      )}
    </div>
  );
}
